import { BaseNodeCommand, type NodeChangeRecord, type INodeMutationManager } from './base/BaseNodeCommand';
import type { Node } from '../models/scene';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import { getTableSchema } from '../../domains/diagram/shapes/table/tableSchemas.registry';
import { mapTableNodeToSchema } from '../../domains/diagram/shapes/table/tableSchemaMapper';

type BatchMutationManager = INodeMutationManager & {
  applyBatchNodeChanges?: (updates: Array<{ nodeId: string; changes: Partial<Node> }>) => NodeChangeRecord[] | undefined;
};

export class ConvertTableSchemaCommand extends BaseNodeCommand {
  constructor(private tableId: string, private targetSchemaId: string) {
    super('Convert Table Schema');
  }

  protected doExecute(): NodeChangeRecord[] {
    const mutationManager = this.getMutationManager() as BatchMutationManager;
    const node = mutationManager.getNode(this.tableId) as Node | undefined;
    if (!node || node.type !== 'table') return [];

    const schema = getTableSchema(this.targetSchemaId);
    if (!schema) return [];

    const conversion = mapTableNodeToSchema(node, schema);

    const nextAttrs = {
      ...(node.attrs ?? {}),
      ...conversion.attrs,
    } as Record<string, unknown>;

    const changes: Partial<Node> = {
      width: conversion.width,
      w: conversion.width,
      height: conversion.height,
      h: conversion.height,
      attrs: nextAttrs,
    };

    const preview = { ...(node as any), ...changes, attrs: nextAttrs } as Node;
    const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, useDiagramStore.getState().nodes).map(
      (update) => ({
        nodeId: update.nodeId,
        changes: { x: update.changes.x, y: update.changes.y } as Partial<Node>,
      }),
    );

    if (dockedUpdates.length > 0 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      return (
        mutationManager.applyBatchNodeChanges([
          { nodeId: this.tableId, changes },
          ...dockedUpdates,
        ]) ?? []
      );
    }

    const record = mutationManager.applyNodeChange(this.tableId, changes);
    return record ? [record] : [];
  }
}

